import { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const lastRef = useRef<{ message: string; time: number }>({ message: '', time: 0 });

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    const now = Date.now();
    // Ignora chamadas duplicadas em menos de 500ms (evita double-invoke do StrictMode)
    if (message === lastRef.current.message && now - lastRef.current.time < 500) return;
    lastRef.current = { message, time: now };

    const id = now;
    setToasts(prev => [...prev.slice(-2), { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }, []);

  const ctx = useMemo(() => ({ showToast }), [showToast]);

  function dismiss(id: number) {
    setToasts(prev => prev.filter(t => t.id !== id));
  }

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div className="fixed bottom-24 left-1/2 -translate-x-1/2 md:bottom-auto md:top-20 md:left-auto md:-translate-x-0 md:right-4 z-[9999] flex flex-col md:flex-col-reverse gap-2 w-[calc(100vw-2rem)] max-w-sm pointer-events-none">
        {toasts.map(toast => (
          <div
            key={toast.id}
            role="status"
            aria-live="polite"
            className={[
              'flex items-start gap-3 rounded-2xl shadow-dropdown px-4 py-3',
              'pointer-events-auto animate-slide-in text-white',
              toast.type === 'success' && 'bg-green-500',
              toast.type === 'error'   && 'bg-red-500',
              toast.type === 'info'    && 'bg-brand',
            ].filter(Boolean).join(' ')}
          >
            <span className="shrink-0 mt-0.5">
              {toast.type === 'success' && <CheckIcon />}
              {toast.type === 'error'   && <XCircleIcon />}
              {toast.type === 'info'    && <InfoIcon />}
            </span>

            <p className="flex-1 text-sm font-medium leading-snug">{toast.message}</p>

            <button
              onClick={() => dismiss(toast.id)}
              aria-label="Fechar"
              className="shrink-0 mt-0.5 opacity-80 hover:opacity-100 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded"
            >
              <CloseIcon />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function CheckIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
function XCircleIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function InfoIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
